import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { EyePrescription, OpticalLabTicket, OpticalLabTicketLens } from '@optiflow/types';
import { useLocale } from '@/hooks/useLocale';
import { formatDate, formatDateTime } from '@/utils/format';
import { useLabTicketQuery } from '../api/opticalJobs.api';
import { PrescriptionOrigin } from '../components/PrescriptionOrigin';
import { trackingErrorMessage } from '../tracking/errors';

/**
 * A job's lab ticket, outside the application shell, ready for the browser to print.
 *
 * It shows what the server put on the ticket and nothing else: manufacturing information, no price, payment, clinical note or examination detail. The prescription grid reads left to right with the right eye first in every language, and the PD is printed exactly as the job holds it — a binocular PD as one figure, a monocular pair as R and L, nothing derived. Printing is the user's choice; the page never prints itself.
 */
export function LabTicketPage() {
  const { t } = useTranslation();
  const { locale, direction } = useLocale();
  const { jobId = '' } = useParams<{ jobId: string }>();
  const { data, isPending, isError, error } = useLabTicketQuery(jobId);

  if (isPending) return <p className="print-status">{t('common.loading')}</p>;
  if (isError || !data) return <p className="print-status">{trackingErrorMessage(t, error)}</p>;

  return (
    <main dir={direction} className="print-root print-root--a4">
      <div className="print-toolbar">
        <button type="button" onClick={() => window.print()}>
          {t('opticalOrders.ticket.print')}
        </button>
        <button type="button" onClick={() => window.close()}>
          {t('opticalOrders.ticket.close')}
        </button>
      </div>
      <article aria-label={t('opticalOrders.ticket.title')}>
        <header className="print-head">
          <h1>{t('opticalOrders.ticket.title')}</h1>
          <p className="print-ticket-ref" dir="ltr">
            {data.jobReference}
          </p>
          <p>{data.branchName}</p>
        </header>

        <dl className="print-meta">
          <Meta label={t('opticalOrders.ticket.order')}>
            <span dir="ltr">{data.orderReference}</span>
          </Meta>
          <Meta label={t('opticalOrders.ticket.ordered')}>
            {formatDate(data.orderedAt, locale)}
          </Meta>
          {data.customer ? (
            <Meta label={t('opticalOrders.ticket.customer')}>
              <span dir="auto">{data.customer.displayName}</span>{' '}
              <span dir="ltr">({data.customer.customerNumber})</span>
            </Meta>
          ) : null}
          <Meta label={t('opticalOrders.ticket.lab')}>
            {data.lab ? (
              <>
                <span dir="auto">{data.lab.name}</span> ·{' '}
                {t(
                  data.lab.isInHouse ? 'opticalOrders.labs.inHouse' : 'opticalOrders.labs.external',
                )}
              </>
            ) : (
              t('opticalOrders.tracking.noLab')
            )}
          </Meta>
          <Meta label={t('opticalOrders.ticket.labReference')}>
            {data.labReference ? <span dir="ltr">{data.labReference}</span> : '—'}
          </Meta>
          <Meta label={t('opticalOrders.ticket.status')}>
            {t(`sales.opticalJobs.status.${data.status}`)}
          </Meta>
        </dl>

        <PrescriptionSection ticket={data} />
        <FrameSection ticket={data} />

        <section className="print-section">
          <h2>{t('opticalOrders.ticket.lenses')}</h2>
          <LensLine eye="OD" lens={data.lenses.right} fitting={data.rightFittingHeightMm} />
          <LensLine eye="OS" lens={data.lenses.left} fitting={data.leftFittingHeightMm} />
        </section>

        {data.labNotes ? (
          <section className="print-section">
            <h2>{t('opticalOrders.ticket.labNotes')}</h2>
            <p dir="auto" style={{ whiteSpace: 'pre-wrap' }}>
              {data.labNotes}
            </p>
          </section>
        ) : null}

        <p className="print-status" style={{ padding: 0 }}>
          {t('opticalOrders.ticket.printed', { when: formatDateTime(data.printedAt, locale) })}
        </p>
      </article>
    </main>
  );
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

const EYE_COLUMNS: { key: keyof EyePrescription; label: string }[] = [
  { key: 'sphere', label: 'SPH' },
  { key: 'cylinder', label: 'CYL' },
  { key: 'axis', label: 'AXIS' },
  { key: 'add', label: 'ADD' },
  { key: 'prism', label: 'Prism' },
  { key: 'prismBase', label: 'Base' },
];

function PrescriptionSection({ ticket }: { ticket: OpticalLabTicket }) {
  const { t } = useTranslation();
  const rx = ticket.prescription;
  return (
    <section className="print-section">
      <h2>{t('opticalOrders.ticket.prescription')}</h2>
      {rx ? (
        <>
          <p>
            <PrescriptionOrigin prescription={rx} />
            {rx.externalReference ? (
              <>
                {' · '}
                <span dir="ltr">{rx.externalReference}</span>
              </>
            ) : null}
          </p>
          <table className="print-table print-mono" dir="ltr">
            <thead>
              <tr>
                <th scope="col">
                  <span className="sr-only">{t('opticalOrders.ticket.eye')}</span>
                </th>
                {EYE_COLUMNS.map((column) => (
                  <th key={column.key} scope="col">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(['OD', 'OS'] as const).map((eye) => {
                const values = eye === 'OD' ? rx.right : rx.left;
                return (
                  <tr key={eye}>
                    <th scope="row">{eye}</th>
                    {EYE_COLUMNS.map((column) => (
                      <td key={column.key}>{values[column.key] ?? '—'}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Each form the job holds, as it holds it: never halved, summed or converted. */}
          <p>
            <strong>PD</strong>
            {rx.pdType ? ` (${t(`measurements.pdTypes.${rx.pdType}`)})` : ''}:{' '}
            {!rx.totalPdMm && !rx.rightPdMm && !rx.leftPdMm ? '—' : null}
            {rx.totalPdMm ? (
              <span data-pd="binocular">
                {t('opticalOrders.ticket.binocular')}{' '}
                <span dir="ltr" className="print-mono">
                  {rx.totalPdMm} mm
                </span>
              </span>
            ) : null}
            {rx.totalPdMm && (rx.rightPdMm || rx.leftPdMm) ? ' · ' : null}
            {rx.rightPdMm || rx.leftPdMm ? (
              <span data-pd="monocular" dir="ltr" className="print-mono">
                R {rx.rightPdMm ?? '—'} mm · L {rx.leftPdMm ?? '—'} mm
              </span>
            ) : null}
          </p>
        </>
      ) : (
        <p>{t('opticalOrders.tracking.noPrescription')}</p>
      )}
    </section>
  );
}

function FrameSection({ ticket }: { ticket: OpticalLabTicket }) {
  const { t } = useTranslation();
  const frame = ticket.frame;
  const recorded = frame && (frame.productName || frame.sku);
  const sizes =
    frame &&
    (frame.lensWidthMm !== null || frame.bridgeWidthMm !== null || frame.templeLengthMm !== null)
      ? `${frame.lensWidthMm ?? '–'}□${frame.bridgeWidthMm ?? '–'} ${frame.templeLengthMm ?? '–'}`
      : null;
  return (
    <section className="print-section">
      <h2>{t('opticalOrders.ticket.frame')}</h2>
      {frame && recorded ? (
        <>
          <p dir="auto">{[frame.productName, frame.variantName].filter(Boolean).join(' — ')}</p>
          <p>
            {frame.sku ? <span dir="ltr">{frame.sku}</span> : null}
            {frame.brandName ? ` · ${frame.brandName}` : ''}
            {frame.colorName ? ` · ${frame.colorName}` : ''}
            {sizes ? (
              <>
                {' · '}
                <span dir="ltr">{sizes}</span>
              </>
            ) : null}
            {frame.rimType ? ` · ${t(`catalog.rimType.${frame.rimType}`)}` : ''}
            {frame.material ? ` · ${t(`catalog.frameMaterial.${frame.material}`)}` : ''}
          </p>
        </>
      ) : (
        <p>{t('opticalOrders.tracking.frameNotRecorded')}</p>
      )}
    </section>
  );
}

function LensLine({
  eye,
  lens,
  fitting,
}: {
  eye: 'OD' | 'OS';
  lens: OpticalLabTicketLens | null;
  fitting: string | null;
}) {
  const { t } = useTranslation();
  const coatings = lens
    ? [
        lens.hasAntiReflective ? t('opticalOrders.job.coatings.antiReflective') : null,
        lens.hasScratchResistant ? t('opticalOrders.job.coatings.scratchResistant') : null,
        lens.hasBlueLightFilter ? t('opticalOrders.job.coatings.blueLight') : null,
        lens.isPhotochromic ? t('opticalOrders.job.coatings.photochromic') : null,
      ].filter(Boolean)
    : [];
  return (
    <div style={{ marginBottom: 6 }}>
      <p>
        <strong dir="ltr">{eye}</strong>{' '}
        {lens ? (
          <span dir="auto">{[lens.productName, lens.variantName].filter(Boolean).join(' — ')}</span>
        ) : (
          t('opticalOrders.tracking.lensNotRecorded')
        )}
      </p>
      {lens ? (
        <p>
          <span dir="ltr">{lens.sku}</span> · {t(`catalog.lensType.${lens.lensType}`)} ·{' '}
          {t(`catalog.lensMaterial.${lens.material}`)}
          {lens.refractiveIndex ? (
            <>
              {' · '}
              <span dir="ltr">{lens.refractiveIndex}</span>
            </>
          ) : null}
          {lens.diameterMm ? (
            <>
              {' · '}
              <span dir="ltr">⌀ {lens.diameterMm} mm</span>
            </>
          ) : null}
          {' · '}
          {t(`opticalOrders.supply.${lens.supplyMode}`)}
          {coatings.length > 0 ? ` · ${coatings.join(' · ')}` : ''}
        </p>
      ) : null}
      <p>
        {t('opticalOrders.job.fittingHeight')}:{' '}
        <span dir="ltr" className="print-mono">
          {fitting ? `${fitting} mm` : '—'}
        </span>
      </p>
    </div>
  );
}
